<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto">
        <form @submit.prevent="saveTicket">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-1/2 mx-auto">
                    <h1 class="stage-page-title">{{ $t('Create ticket') }}</h1>
                </div>
            </div>
            <div class="stage-page-body">
                <div class="w-full xl:w-1/2 mx-auto bg-white shadow rounded-md overflow-hidden border border-ultralight">
                    <loading :status="loading.form"/>
                    <div class="p-6">
                        <div class="grid grid-cols-3 gap-6">
                            <div class="col-span-3">
                                <label class="stage-filter-label block mb-2" for="subject">{{ $t('Subject') }}</label>
                                <div class="relative">
                                    <input
                                        id="subject"
                                        v-model="ticket.subject"
                                        :placeholder="$t('Subject')"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        required
                                    >
                                </div>
                            </div>
                            <div class="col-span-3">
                                <label class="stage-filter-label block mb-2" for="project_ids">{{ $t('Project (optional)') }}</label>
                                <div class="relative">
                                    <input-select
                                        id="project_ids"
                                        v-model="ticket.project_ids"
                                        class="stage-filter-select"
                                        multiple
                                        :options="projectList"
                                        option-label="title"
                                    >
                                        <template v-slot:selectedOption="props">
                                            <template v-if="props && !props.anySelected">
                                                <span class="block truncate">{{ $t('Select project') }}</span>
                                            </template>
                                            <template v-else>
                                                <div class="flex items-center space-x-3 min-w-0">
                                                    <img
                                                        :src="projectImageUrl(props.option)"
                                                        :alt="props.option.title || 'Projektbild'"
                                                        class="h-6 w-6 rounded-sm object-cover flex-shrink-0"
                                                    >
                                                    <span class="block truncate">{{ props.option.title }}</span>
                                                </div>
                                            </template>
                                        </template>
                                        <template v-slot:selectOption="props">
                                            <div class="flex items-center space-x-3 min-w-0">
                                                <input
                                                    type="checkbox"
                                                    class="form-checkbox h-4 w-4 text-gray-600 pointer-events-none flex-shrink-0"
                                                    :checked="isProjectSelected(props.option.id)"
                                                >
                                                <img
                                                    :src="projectImageUrl(props.option)"
                                                    :alt="props.option.title || 'Projektbild'"
                                                    class="h-6 w-6 rounded-sm object-cover flex-shrink-0"
                                                >
                                                <div :class="isProjectSelected(props.option.id) ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                    {{ props.option.title }}
                                                </div>
                                            </div>
                                        </template>
                                        <template v-slot:notFound>
                                            <div class="flex items-center space-x-3">
                                                <div class="font-normal block truncate font-normal">
                                                    {{ $t('No projects available') }}
                                                </div>
                                            </div>
                                        </template>
                                    </input-select>
                                </div>
                            </div>
                            <div v-if="ticketTopicList.length > 0" class="col-span-3">
                                <label class="stage-filter-label block mb-2" for="ticket_topic">{{ $t('TicketTopic') }}</label>
                                <div class="relative">
                                    <input-select
                                        id="ticket_topic"
                                        v-model="ticket.ticket_topic_id"
                                        class="stage-filter-select"
                                        :options="ticketTopicList"
                                        option-label="name"
                                        required
                                    />
                                </div>
                            </div>
                            <div v-if="mainAdminUser" class="col-span-3">
                                <label class="stage-filter-label block mb-2" for="ticket_recipient_readonly">{{ $t('Recipient') }}</label>
                                <div
                                    id="ticket_recipient_readonly"
                                    class="stage-filter-input flex items-center px-3 py-3 bg-gray-50 border border-bordergray-100 rounded-sm"
                                >
                                    <img
                                        alt="Profilbild"
                                        :src="mainAdminUser.avatar !== 'gravatar' ? mainAdminUser.avatar : mainAdminUser.gravatar"
                                        class="h-6 w-6 rounded-full object-cover mr-2 flex-shrink-0"
                                    />
                                    <div class="min-w-0">
                                        <div class="text-sm font-base text-gray-900 truncate">{{ mainAdminUser.name }}</div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="assignableUsers.length > 0" class="col-span-3">
                                <label class="stage-filter-label block mb-2" for="ticket_customer_ids">{{ $t('Include users') }}</label>
                                <div
                                    id="ticket_customer_ids"
                                    class="stage-filter-input border border-bordergray-100 rounded-sm max-h-56 overflow-y-auto"
                                >
                                    <label
                                        v-for="customer in assignableUsers"
                                        :key="'new-ticket-customer-' + customer.id"
                                        class="flex items-center px-3 py-2 text-sm text-gray-700 cursor-pointer"
                                    >
                                        <input
                                            v-model="ticket.customer_ids"
                                            :value="customer.id"
                                            class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-3"
                                            type="checkbox"
                                        >
                                        <img
                                            :alt="$t('Avatar')"
                                            :src="customer.avatar !== 'gravatar' ? customer.avatar : customer.gravatar"
                                            class="h-6 w-6 rounded-full object-cover mr-2"
                                        />
                                        <span class="block truncate">{{ customer.name }}</span>
                                    </label>
                                </div>
                            </div>
                            <div class="col-span-3">
                                <label class="stage-filter-label block mb-2" for="ticket_body">{{ $t('Ticket body') }}</label>
                                <div class="relative ticket-new-body-editor">
                                    <input-wysiwyg
                                        id="ticket_body"
                                        v-model="ticket.body"
                                        :minHeight="520"
                                        :plugins="{images: true, attachment: true}"
                                        @selectUploadFile="selectUploadFile"
                                    >
                                        <template v-slot:top>
                                            <div :class="{'bg-gray-200': uploadingFileProgress > 0}" class="h-1 w-full">
                                                <div :style="{width: uploadingFileProgress + '%'}" class="bg-blue-500 py-0.5"></div>
                                            </div>
                                        </template>
                                    </input-wysiwyg>
                                </div>
                            </div>
                            <div v-if="ticket.attachments.length > 0" class="col-span-3">
                                <div class="flex flex-wrap items-start gap-2">
                                    <template v-for="(attachment, index) in ticket.attachments">
                                        <attachment :details="attachment" v-on:remove="removeAttachment(index)"/>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 text-right px-6 py-3 border-t border-ultralight">
                        <div class="inline-flex">
                            <router-link
                                class="btn-stage-secondary mr-3"
                                to="/tickets/list"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                class="btn-stage-primary"
                                type="submit"
                            >
                                {{ $t('Create ticket') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <input ref="fileInput" hidden type="file" @change="uploadFile($event)">
    </main>
</template>

<script>
export default {
    name: "index",
    metaInfo() {
        return {
            title: this.$i18n.t('New ticket')
        }
    },
    data() {
        return {
            loading: {
                form: false,
                file: false,
            },
            uploadingFileProgress: 0,
            mainAdminUser: null,
            ticket: {
                subject: null,
                project_ids: [],
                ticket_topic_id: null,
                customer_ids: [],
                body: '',
                attachments: [],
            },
            ticketTopicList: [],
            assignableUsers: [],
            projectList: [],
        }
    },
    mounted() {
        this.getTicketTopics();
        this.getMainAdmin();
        this.getAssignableUsers();
        this.getProjects();
    },
    methods: {
        normalizeCollection(collection) {
            if (Array.isArray(collection)) {
                return collection;
            }
            if (collection && Array.isArray(collection.data)) {
                return collection.data;
            }
            return [];
        },
        getTicketTopics() {
            const self = this;
            self.loading.form = true;
            axios.get('api/tickets/ticket-topics').then(function (response) {
                self.ticketTopicList = response.data;
                self.loading.form = false;
            }).catch(function () {
                self.loading.form = false;
            });
        },
        getMainAdmin() {
            const self = this;
            axios.get('api/tickets/main-admin').then(function (response) {
                self.mainAdminUser = response.data || null;
            }).catch(function () {
                self.mainAdminUser = null;
            });
        },
        getAssignableUsers() {
            const self = this;
            axios.get('api/tickets/assignable-users').then(function (response) {
                self.assignableUsers = self.normalizeCollection(response.data);
            }).catch(function () {
                self.assignableUsers = [];
            });
        },
        getProjects() {
            const self = this;
            axios.get('api/tickets/projects').then(function (response) {
                self.projectList = self.normalizeCollection(response.data);
            }).catch(function () {
                self.projectList = [];
            });
        },
        projectImageUrl(project) {
            if (project && project.image_url) {
                return project.image_url;
            }

            return '/images/default/icon.png';
        },
        isProjectSelected(projectId) {
            return this.ticket.project_ids
                .map(function (selectedProjectId) {
                    return Number(selectedProjectId);
                })
                .indexOf(Number(projectId)) > -1;
        },
        saveTicket() {
            const self = this;
            self.loading.form = true;
            axios.post('api/tickets', self.ticket).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/tickets/' + response.data.ticket.id);
            }).catch(function () {
                self.loading.form = false;
            });
        },
        selectUploadFile() {
            if (!this.loading.file) {
                this.$refs.fileInput.click();
            } else {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('A file is being uploaded').toString(),
                    type: 'warning'
                });
            }
        },
        uploadFile(e) {
            const self = this;
            const formData = new FormData();
            self.loading.file = true;
            formData.append('file', e.target.files[0]);
            axios.post(
                'api/tickets/attachments',
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: function (progressEvent) {
                        self.uploadingFileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    }.bind(this)
                }
            ).then(function (response) {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
                self.ticket.attachments.push(response.data);
            }).catch(function () {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
            });
        },
        removeAttachment(attachment) {
            this.ticket.attachments.splice(attachment, 1);
        }
    }
}
</script>

<style lang="scss">
.stage-filter-select > div > .inline-block {
    box-shadow: none;
    border-radius: 4px;
}

.stage-filter-select > div > .inline-block > button {
    border-color: var(--ui-border-default);
    border-radius: 4px;
    box-shadow: none;
}

.stage-filter-select > div > .absolute {
    border-radius: 4px;
    box-shadow: none;
    border: 1px solid var(--ui-border-default);
}

.stage-filter-select > div > .absolute > ul {
    box-shadow: none;
}

.ticket-new-body-editor .tox .tox-sidebar-wrap,
.ticket-new-body-editor .tox .tox-edit-area {
    min-height: 520px !important;
}

.ticket-new-body-editor .tox .tox-edit-area__iframe {
    min-height: 520px !important;
}
</style>
